import { Box } from "@mui/material"
import React, { useState, useEffect, useRef } from "react"
import { VideoType } from "../services/api/video"
import { useSearchParams } from "react-router-dom"
import { VideoContextMenu } from "./VideoContextMenu"

interface VideoPlayerProps {
  video?: VideoType
  resolution?: number
  raw?: boolean
  autoplay?: boolean
  disableKeyboardShortcuts?: boolean
  onThumbnailChange?: () => void
  onVideoEnd?: () => void
}

export const VideoPlayer = ({
  video,
  resolution = 1080,
  raw = false,
  autoplay = true,
  disableKeyboardShortcuts = false,
  onThumbnailChange,
  onVideoEnd,
}: VideoPlayerProps): React.ReactElement => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [loading, setLoading] = useState(true)
  const [loop, setLoop] = useState(false)
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number
    mouseY: number
  } | null>(null)

  const [searchParams, _] = useSearchParams()

  useEffect(() => {
    if (videoRef.current && video) {
      const originalSrc = `/api/media/video/${video.id}/${raw ? 0 : resolution}`
      videoRef.current.src = originalSrc
      videoRef.current.load()
    }
  }, [video?.id, raw, resolution])

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [disableKeyboardShortcuts])

  useEffect(() => {
    if (searchParams.get("at") && videoRef.current) {
      videoRef.current.currentTime = parseInt(searchParams.get("at") || "0")
    }
  }, [searchParams.get("at")])

  const handleCanPlay = () => {
    setLoading(false)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!videoRef.current) return
    if (disableKeyboardShortcuts) return

    switch (e.key.toLowerCase()) {
      case " ":
        e.preventDefault()
        if (videoRef.current.paused) {
          videoRef.current.play()
        } else {
          videoRef.current.pause()
        }
        break

      case "f":
        if (document.fullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen()
          }
        } else {
          if (videoRef.current.requestFullscreen) {
            videoRef.current.requestFullscreen()
          }
        }
        break

      case "m":
        e.preventDefault()
        videoRef.current.muted = !videoRef.current.muted
        break

      case "arrowright":
        if (videoRef.current.paused) {
          e.preventDefault()
          videoRef.current.currentTime += 1 / 25
        }
        break

      case "arrowleft":
        if (videoRef.current.paused) {
          e.preventDefault()
          videoRef.current.currentTime -= 1 / 25
        }
        break

      case "arrowup":
        e.preventDefault()
        videoRef.current.volume = Math.min(videoRef.current.volume + 0.1, 1)
        break

      case "arrowdown":
        e.preventDefault()
        videoRef.current.volume = Math.max(videoRef.current.volume - 0.1, 0)
        break

      default:
        break
    }
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null
    )
  }

  const handleClose = () => {
    setContextMenu(null)
  }

  return (
    <>
      <video
        ref={videoRef}
        controls
        autoPlay={autoplay}
        onCanPlay={handleCanPlay}
        onContextMenu={handleContextMenu}
        loop={loop}
        onEnded={onVideoEnd}
        tabIndex={-1}
        style={{ maxHeight: "70vh", width: "100%", aspectRatio: loading ? "16/9" : "auto" }}
      />
      {video && videoRef.current && (
        <VideoContextMenu
          video={video}
          onClose={handleClose}
          open={Boolean(contextMenu)}
          position={contextMenu ? { x: contextMenu.mouseX, y: contextMenu.mouseY } : null}
          loop={loop}
          setLoop={setLoop}
          videoRef={videoRef}
          onThumbnailChange={onThumbnailChange}
        />
      )}
    </>
  )
}
